'use client';

import { useEffect, useMemo, useState } from 'react';
import type { ToolExecutionHar } from '@terra/shared-contracts-tracker';
import type { StoredHarDisplayEntry } from '@terra/shared-utils';
import { useChatHarOptional } from '../chat/har/ChatHarProvider';

export const useEnrichedToolHarEntry = ({
  har,
}: {
  har: ToolExecutionHar;
}): {
  harEntry: StoredHarDisplayEntry;
  isResolving: boolean;
} => {
  const chatHar = useChatHarOptional();
  const enrichHarEntry = chatHar?.enrichHarEntryForDisplay;
  const [resolvedHarEntry, setResolvedHarEntry] =
    useState<StoredHarDisplayEntry | null>(null);
  const [isResolving, setIsResolving] = useState(Boolean(enrichHarEntry));

  const baseHarEntry = useMemo(
    () => har.harEntry as StoredHarDisplayEntry,
    [har.harEntry],
  );

  useEffect(() => {
    if (!enrichHarEntry) {
      setResolvedHarEntry(null);
      setIsResolving(false);

      return;
    }

    let cancelled = false;

    setIsResolving(true);

    void enrichHarEntry({ har })
      .then((entry) => {
        if (!cancelled) {
          setResolvedHarEntry(entry);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedHarEntry(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsResolving(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enrichHarEntry, har]);

  return {
    harEntry: resolvedHarEntry ?? baseHarEntry,
    isResolving,
  };
};
